import React, { useState, cloneElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, MessageCircle, ChevronDown, Activity, Bone, PersonStanding, HeartPulse, Sparkles, Cpu, MapPin } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function Header() {
  const {
    lang,
    toggleLang,
    t
  } = useLanguage();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const handleMouseEnter = (menu: string) => setActiveMenu(menu);
  const handleMouseLeave = () => {
    setActiveMenu(null);
    setHoveredItem(null);
  };
  const serviciosItems = [{
    icon: <Activity data-id="element-99" />,
    title: t('services.sports'),
    desc: t('services.desc.sports')
  }, {
    icon: <Bone data-id="element-100" />,
    title: t('services.ortho'),
    desc: t('services.desc.ortho')
  }, {
    icon: <PersonStanding data-id="element-101" />,
    title: t('services.rehab'),
    desc: t('services.desc.rehab')
  }, {
    icon: <HeartPulse data-id="element-102" />,
    title: t('services.geriatrics'),
    desc: t('services.desc.geriatrics')
  }, {
    icon: <Sparkles data-id="element-103" />,
    title: t('services.aesthetic'),
    desc: t('services.desc.aesthetic')
  }];
  const ubicacionesItems = [{
    icon: <MapPin data-id="element-104" />,
    title: t('loc.polanco'),
    desc: t('locations.address.polanco')
  }, {
    icon: <MapPin data-id="element-105" />,
    title: t('loc.santafe'),
    desc: t('locations.address.santafe')
  }, {
    icon: <MapPin data-id="element-106" />,
    title: t('loc.lomas'),
    desc: t('locations.address.lomas')
  }];
  return <header className="fixed top-4 left-4 right-4 z-50" data-id="element-107">
      <div className="max-w-7xl mx-auto bg-white/80 backdrop-blur-xl rounded-full shadow-[0_4px_30px_rgba(0,0,0,0.08)] border border-white/60 px-6 lg:px-8" data-id="element-108">
        <div className="flex justify-between items-center h-20" data-id="element-109">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" data-id="element-110">
            <img src="https://cdn.magicpatterns.com/uploads/ppUnKnYhBKTX8zx2E76sSP/ISO_ERGOCENTER_1.svg" alt="Body•On Logo" className="h-9 w-9 object-contain" data-id="element-111" />
            <span className="font-heading font-bold text-[23px] tracking-wide ml-3 bg-gradient-to-r from-[#2B7A8F] via-[#30BBA8] to-[#5CD4C2] bg-clip-text text-transparent" data-id="element-112">
              Body • On
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-7" data-id="element-113">
            {/* Servicios Menu */}
            <div className="relative h-20 flex items-center" onMouseEnter={() => handleMouseEnter('servicios')} onMouseLeave={handleMouseLeave} data-id="element-114">
              <button className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-bodyon-teal transition-colors" data-id="element-115">
                {t('nav.services')} <ChevronDown className="w-3.5 h-3.5" data-id="element-116" />
              </button>
            </div>

            {/* Tecnología Menu */}
            <div className="relative h-20 flex items-center" onMouseEnter={() => handleMouseEnter('tecnologia')} onMouseLeave={handleMouseLeave} data-id="element-117">
              <button className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-bodyon-teal transition-colors" data-id="element-118">
                {t('nav.technology')} <ChevronDown className="w-3.5 h-3.5" data-id="element-119" />
              </button>
            </div>

            <a href="#beneficios" className="text-sm font-medium text-gray-600 hover:text-bodyon-teal transition-colors" data-id="element-120">
              {t('nav.benefits')}
            </a>

            <a href="#nosotros" className="text-sm font-medium text-gray-600 hover:text-bodyon-teal transition-colors" data-id="element-121">
              {t('nav.about')}
            </a>

            {/* Ubicaciones Menu */}
            <div className="relative h-20 flex items-center" onMouseEnter={() => handleMouseEnter('ubicaciones')} onMouseLeave={handleMouseLeave} data-id="element-122">
              <button className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-bodyon-teal transition-colors" data-id="element-123">
                {t('nav.locations')} <ChevronDown className="w-3.5 h-3.5" data-id="element-124" />
              </button>
            </div>

            <a href="#contacto" className="text-sm font-medium text-gray-600 hover:text-bodyon-teal transition-colors" data-id="element-125">
              {t('nav.contact')}
            </a>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3" data-id="element-126">
            {/* Language Toggle - pill segment style */}
            <div className="flex items-center bg-gray-100 rounded-full p-1" data-id="element-127">
              <button onClick={() => lang !== 'es' && toggleLang()} className={`px-3 py-1 text-xs font-semibold transition-all duration-200 rounded-full ${lang === 'es' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-128">
                ES
              </button>
              <button onClick={() => lang !== 'en' && toggleLang()} className={`px-3 py-1 text-xs font-semibold transition-all duration-200 rounded-full ${lang === 'en' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-129">
                EN
              </button>
            </div>

            <a href="https://wa.me/" target="_blank" rel="noopener noreferrer" className="hidden md:flex items-center gap-2 bg-bodyon-teal hover:bg-bodyon-teal-dark text-white px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300" data-id="element-130">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" data-id="element-131">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-132" />
              </svg>
              {t('cta.schedule')}
            </a>
          </div>
        </div>
      </div>

      {/* Full-width Megamenu Dropdowns */}
      <AnimatePresence data-id="element-133">
        {activeMenu === 'servicios' && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.2
      }} onMouseEnter={() => setActiveMenu('servicios')} onMouseLeave={handleMouseLeave} className="absolute top-[calc(100%+8px)] left-0 right-0 flex justify-center px-4" data-id="element-134">
            <div className="w-full max-w-6xl bg-[#F3F4F6] rounded-3xl shadow-[0_12px_48px_rgba(0,0,0,0.1)] overflow-hidden flex" data-id="element-135">
              {/* Left: Photo Panel */}
              <div className="w-[320px] shrink-0 relative rounded-2xl overflow-hidden m-3" data-id="element-136">
                <img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80" alt="Medical Care" className="absolute inset-0 w-full h-full object-cover" data-id="element-137" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-138" />
                <div className="relative z-10 p-8 flex flex-col justify-end h-full" data-id="element-139">
                  <h3 className="font-heading font-bold text-2xl text-white leading-tight mb-3" data-id="element-140">
                    {lang === 'es' ? 'Atención integral para tu bienestar' : 'Comprehensive care for your wellness'}
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-141">
                    {lang === 'es' ? 'Cuidado de alta calidad diseñado en torno a tu comodidad y respeto.' : 'High-quality care designed around comfort and respect.'}
                  </p>
                </div>
              </div>

              {/* Right: Service Items */}
              <div className="flex-1 p-6 flex flex-col" data-id="element-142">
                <div className="flex items-center justify-between mb-4 px-2" data-id="element-143">
                  <span className="text-sm font-semibold text-gray-500" data-id="element-144">
                    {lang === 'es' ? 'Servicios Especializados' : 'Specialized Services'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 flex-1" data-id="element-145">
                  {serviciosItems.map((item, index) => <MegaMenuCard key={index} icon={item.icon} title={item.title} desc={item.desc} isHovered={hoveredItem === `s-${index}`} onHover={() => setHoveredItem(`s-${index}`)} onLeave={() => setHoveredItem(null)} data-id="element-146" />)}
                </div>

                {/* Bottom CTA */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 px-2" data-id="element-147">
                  <div data-id="element-148">
                    <p className="text-sm font-semibold text-bodyon-teal-dark" data-id="element-149">
                      {lang === 'es' ? '¿Necesitas ayuda para elegir?' : 'Need help choosing a service?'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-150">
                      {lang === 'es' ? 'Te ayudamos a encontrar el tratamiento ideal.' : 'We help you find the ideal treatment.'}
                    </p>
                  </div>
                  <a href="https://wa.me/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-bodyon-teal-dark text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-bodyon-teal transition-colors" data-id="element-151">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" data-id="element-152">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-153" />
                    </svg>
                    {t('cta.schedule')}
                  </a>
                </div>
              </div>
            </div>
          </motion.div>}

        {activeMenu === 'tecnologia' && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.2
      }} onMouseEnter={() => setActiveMenu('tecnologia')} onMouseLeave={handleMouseLeave} className="absolute top-[calc(100%+8px)] left-0 right-0 flex justify-center px-4" data-id="element-154">
            <div className="w-full max-w-3xl bg-[#F3F4F6] rounded-3xl shadow-[0_12px_48px_rgba(0,0,0,0.1)] overflow-hidden flex" data-id="element-155">
              {/* Left: Photo Panel */}
              <div className="w-[280px] shrink-0 relative rounded-2xl overflow-hidden m-3" data-id="element-156">
                <img src="https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80" alt="Technology" className="absolute inset-0 w-full h-full object-cover" data-id="element-157" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-158" />
                <div className="relative z-10 p-8 flex flex-col justify-end h-full" data-id="element-159">
                  <h3 className="font-heading font-bold text-xl text-white leading-tight mb-2" data-id="element-160">
                    {lang === 'es' ? 'Tecnología de Vanguardia' : 'Cutting-Edge Technology'}
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-161">
                    {lang === 'es' ? 'Innovación al servicio de tu salud.' : 'Innovation at the service of your health.'}
                  </p>
                </div>
              </div>

              {/* Right: Tech Item */}
              <div className="flex-1 p-6 flex flex-col" data-id="element-162">
                <span className="text-sm font-semibold text-gray-500 mb-4 px-2" data-id="element-163">
                  {t('nav.technology')}
                </span>
                <MegaMenuCard icon={<Cpu data-id="element-165" />} title={t('tech.advanced')} desc={lang === 'es' ? 'Estimulación celular con frecuencias específicas para reparar tejidos.' : 'Cellular stimulation with specific frequencies to repair tissues.'} isHovered={hoveredItem === 'tech-0'} onHover={() => setHoveredItem('tech-0')} onLeave={() => setHoveredItem(null)} data-id="element-164" />
              </div>
            </div>
          </motion.div>}

        {activeMenu === 'ubicaciones' && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.2
      }} onMouseEnter={() => setActiveMenu('ubicaciones')} onMouseLeave={handleMouseLeave} className="absolute top-[calc(100%+8px)] left-0 right-0 flex justify-center px-4" data-id="element-166">
            <div className="w-full max-w-4xl bg-[#F3F4F6] rounded-3xl shadow-[0_12px_48px_rgba(0,0,0,0.1)] overflow-hidden flex" data-id="element-167">
              {/* Left: Photo Panel */}
              <div className="w-[280px] shrink-0 relative rounded-2xl overflow-hidden m-3" data-id="element-168">
                <img src="https://images.unsplash.com/photo-1585399000684-d2f72660f092?auto=format&fit=crop&w=600&q=80" alt="Mexico City" className="absolute inset-0 w-full h-full object-cover" data-id="element-169" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-170" />
                <div className="relative z-10 p-8 flex flex-col justify-end h-full" data-id="element-171">
                  <h3 className="font-heading font-bold text-xl text-white leading-tight mb-2" data-id="element-172">
                    {lang === 'es' ? 'Encuéntranos en CDMX' : 'Find Us in Mexico City'}
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-173">
                    {lang === 'es' ? 'Tres ubicaciones estratégicas para tu comodidad.' : 'Three strategic locations for your convenience.'}
                  </p>
                </div>
              </div>

              {/* Right: Location Items */}
              <div className="flex-1 p-6 flex flex-col" data-id="element-174">
                <span className="text-sm font-semibold text-gray-500 mb-4 px-2" data-id="element-175">
                  {t('nav.locations')}
                </span>
                <div className="flex flex-col gap-2" data-id="element-176">
                  {ubicacionesItems.map((item, index) => <MegaMenuCard key={index} icon={item.icon} title={item.title} desc={item.desc} isHovered={hoveredItem === `loc-${index}`} onHover={() => setHoveredItem(`loc-${index}`)} onLeave={() => setHoveredItem(null)} data-id="element-177" />)}
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}
function MegaMenuCard({
  icon,
  title,
  desc,
  isHovered,
  onHover,
  onLeave
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
}) {
  return <div onMouseEnter={onHover} onMouseLeave={onLeave} className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all duration-200 ${isHovered ? 'bg-white shadow-[0_2px_12px_rgba(0,0,0,0.06)]' : 'hover:bg-white/60'}`} data-id="element-178">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 border transition-all duration-200 ${isHovered ? 'bg-white border-gray-200 text-bodyon-teal-dark shadow-sm' : 'bg-white border-gray-200 text-gray-500'}`} data-id="element-179">
        {cloneElement(icon as React.ReactElement, {
        className: 'w-5 h-5'
      })}
      </div>
      <div className="min-w-0" data-id="element-180">
        <span className={`text-sm font-semibold block transition-colors duration-200 ${isHovered ? 'text-bodyon-teal-dark' : 'text-bodyon-teal-dark/80'}`} data-id="element-181">
          {title}
        </span>
        <span className="text-xs text-gray-500 line-clamp-1" data-id="element-182">{desc}</span>
      </div>
    </div>;
}